/** User-facing copy for workspace MCP servers and skills. STE. */

export type AgentResourceInstruction =
  | string
  | {
      before: string;
      href: string;
      label: string;
      after: string;
    };

export const GITHUB_PERSONAL_ACCESS_TOKEN_URL = "https://github.com/settings/personal-access-tokens/new";
export const CIRCLECI_PERSONAL_API_TOKEN_URL = "https://app.circleci.com/settings/user/tokens";

export const AGENT_RESOURCES_COPY = {
  mcpTitle: "MCP servers",
  skillsTitle: "Skills",
  mcpHelper:
    "Agents on every run in this workspace can use these MCP servers. One signed-in account is shared by every agent.",
  skillsHelper: "Agents on every run in this workspace can use these skills.",
  refinementNote:
    "These MCP servers can change systems outside SuperPlane during refinement. The repository stays read-only.",
  addConnection: "Add MCP server",
  addServerTitle: (label: string) => `Add ${label}`,
  editConnection: "Edit MCP server",
  dialogDescription: "Agents on every run in this workspace can use this MCP server.",
  catalogDescription: "Choose a common server, or add a custom MCP URL.",
  catalogSearchPlaceholder: "Search servers",
  catalogSearchEmpty: "No servers match this search.",
  catalogCustom: "Add custom",
  addSkill: "Add skill",
  editSkill: "Edit skill",
  skillDialogDescription: "Agents on every run in this workspace can use this SKILL.md file.",
  skillExtraFilesNote: "Skills that need extra files are not supported.",
  emptyConnectionsTitle: "No MCP servers yet",
  emptyConnectionsBody: "Add an MCP server so agents can use it on every run.",
  unnamedResource: "MCP server",
  unnamedSkill: "skill",
  deleteDescription: "This removes the MCP server for every agent in this workspace.",
  deleteSkillDescription: "This removes the skill for every agent in this workspace.",
  emptySkillsTitle: "No skills yet",
  emptySkillsBody: "Add a SKILL.md so agents can use it on every run.",
  skillSourceInline: "SKILL.md",
  skillCommandLabel: "Command",
  skillCommandHelper: (name: string) => `Agents can call this skill with /${name}.`,
  skillCommandEmptyHelper: "Type a name. SuperPlane suggests the slash command.",
  skillNameHelper: "SuperPlane builds the command from this name.",
  authHeader: "Header",
  authSignIn: "Sign-in",
  statusConnected: "Connected",
  statusNotConnected: "Not connected",
  statusReconnect: "Reconnect",
  statusReady: "Ready",
  statusFetchFailed: "Failed to fetch",
  toolsEmpty: "This server has no tools.",
  toolsLoading: "Loading tools...",
  toolsLoadError: "SuperPlane could not load the tools. Try again.",
  toolsSortLabel: "Sort tools",
  toolsSortName: "Name",
  toolsSortRead: "Read first",
  toolsSortWrite: "Write first",
  toolsToggle: "Tools",
  toolRead: "Read",
  toolWrite: "Write",
  toolsCount: (enabled: number, available: number) => `${enabled}/${available}`,
  enableToolLabel: (name: string) => `Enable ${name}`,
  close: "Close",
  connect: "Connect",
  signIn: "Sign in",
  reconnect: "Reconnect",
  disconnect: "Disconnect",
  edit: "Edit",
  delete: "Delete",
  saveConnection: "Save MCP server",
  saveSkill: "Save skill",
  cancel: "Cancel",
  nameLabel: "Name",
  nameHelper: "Use lowercase letters, numbers, and dashes. The name superplane is reserved.",
  urlLabel: "URL",
  urlHelper: "Use an https URL. Private hosts are not allowed.",
  markdownLabel: "SKILL.md",
  markdownHelper: "Paste the skill markdown. Extra files are not included.",
  authLabel: "Authentication",
  headersLabel: "Headers",
  headerNameLabel: "Header name",
  headerSecretLabel: "Secret",
  tokenLabel: "Personal access token",
  tokenRequired: "Personal access token is required.",
  addHeader: "Add header",
  removeHeader: "Remove header",
  loading: "Loading MCP servers...",
  skillsLoading: "Loading skills...",
  loadError: "SuperPlane could not load MCP servers. Try again.",
  skillsLoadError: "SuperPlane could not load skills. Try again.",
  nameRequired: "Name is required.",
  nameTaken: "This command is already in use.",
  nameInvalid: "Use a lowercase name that starts with a letter. Use letters, numbers, and dashes.",
  nameReserved: "The name superplane is reserved.",
  urlRequired: "URL is required.",
  urlInvalid: "Enter an https URL.",
  markdownRequired: "SKILL.md content is required.",
  headersHelper: "Add a header only when the server needs a credential.",
  githubInstruction: {
    before: "Create a ",
    href: GITHUB_PERSONAL_ACCESS_TOKEN_URL,
    label: "GitHub personal access token",
    after: " and paste it here.",
  },
  jiraInstruction: "Sign in with Atlassian to grant access to Jira.",
  linearInstruction: "Sign in with Linear.",
  circleciInstruction: {
    before: "Create a ",
    href: CIRCLECI_PERSONAL_API_TOKEN_URL,
    label: "CircleCI personal API token",
    after: " and paste it here.",
  },
  semaphoreInstruction: "Ask Semaphore support to enable MCP for your organization, then sign in.",
  sentryInstruction: "Sign in with Sentry.",
  headerIncomplete: "Each header needs a name and a secret.",
  created: "MCP server added.",
  updated: "MCP server updated.",
  deleted: "MCP server deleted.",
  skillCreated: "Skill added.",
  skillUpdated: "Skill updated.",
  skillDeleted: "Skill deleted.",
  disconnected: "MCP server disconnected.",
  urlTaken: "This MCP server is already connected.",
  createFailed: "SuperPlane could not add the MCP server.",
  updateFailed: "SuperPlane could not update the MCP server.",
  deleteFailed: "SuperPlane could not delete the MCP server.",
  skillCreateFailed: "SuperPlane could not add the skill.",
  skillUpdateFailed: "SuperPlane could not update the skill.",
  skillDeleteFailed: "SuperPlane could not delete the skill.",
  skillNotFound: "This skill does not exist.",
  connectFailed: "SuperPlane could not start sign-in.",
  disconnectFailed: "SuperPlane could not disconnect the MCP server.",
  noUpdatePermission: "You do not have permission to update this workspace.",
} as const;
